<template>
  <div class="approval-record-container">
    <div class="search-container">
      <el-form :inline="true" :model="searchForm" size="small">
        <el-form-item label="工单编号">
          <el-input
            v-model="searchForm.order_no"
            placeholder="请输入工单编号"
            clearable
          />
        </el-form-item>
        <el-form-item label="处理人">
          <el-input
            v-model="searchForm.handler_name"
            placeholder="请输入处理人"
            clearable
          />
        </el-form-item>
        <el-form-item label="审批状态">
          <el-select v-model="searchForm.approval_status" placeholder="请选择状态" clearable>
            <el-option label="待处理" :value="1" />
            <el-option label="已通过" :value="2" />
            <el-option label="已驳回" :value="3" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="table-container">
      <el-table
        v-loading="loading"
        :data="tableData"
        style="width: 100%"
        :cell-style="{ textAlign: 'center' }"
        :header-cell-style="{ textAlign: 'center', backgroundColor: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column type="selection" width="55" align="center" />
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="order_no" label="工单编号" width="180" align="center">
          <template slot-scope="{ row }">
            <el-link type="primary" :underline="false" @click="handleViewOrder(row)">{{ row.order_no }}</el-link>
          </template>
        </el-table-column>
        <el-table-column prop="definition_name" label="流程名称" width="180" align="center">
          <template slot-scope="{ row }">
            <div class="process-info">
              <span>{{ row.definition_name }}</span>
              <div class="process-version">版本: v{{ row.version || '1.0' }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="node_name" label="节点名称" width="200" align="center">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="getNodeTagType(row.node_type)">{{ row.node_name }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="handler_name" label="处理人" width="120" align="center" />
        <el-table-column prop="handler_dept" label="处理部门" width="120" align="center" />
        <el-table-column label="审批状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="getStatusType(row.approval_status)" size="small">
              {{ getStatusText(row.approval_status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="approval_comment" label="审批意见" width="200" align="center">
          <template slot-scope="{ row }">
            <div class="comment-text">
              {{ row.approval_comment || '-' }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="start_time" label="审批时间" width="160" align="center" />
        <el-table-column prop="duration" label="处理时长" width="120" align="center">
          <template slot-scope="{ row }">
            <span>{{ calculateDuration(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" icon="el-icon-view" @click="handleView(row)">查看</el-button>
            <el-button
              v-if="row.approval_status === 1 && row.can_withdraw"
              type="text"
              size="small"
              icon="el-icon-refresh-left"
              @click="handleWithdraw(row)"
            >
              撤回
            </el-button>
            <el-dropdown trigger="click" @command="handleAction($event, row)">
              <el-button type="text" size="small" icon="el-icon-more">更多<i class="el-icon-arrow-down el-icon--right"></i></el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="flowchart" icon="el-icon-share">流程图</el-dropdown-item>
                <el-dropdown-item command="history" icon="el-icon-time">审批历史</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.size"
          :total="pagination.total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <!-- 审批详情对话框 -->
    <el-dialog
      title="审批详情"
      :visible.sync="detailDialogVisible"
      width="600px"
    >
      <div v-if="currentRecord" class="approval-detail">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="工单编号">{{ currentRecord.order_no }}</el-descriptions-item>
          <el-descriptions-item label="节点名称">{{ currentRecord.node_name }}</el-descriptions-item>
          <el-descriptions-item label="处理人">{{ currentRecord.handler_name }}</el-descriptions-item>
          <el-descriptions-item label="审批状态">
            <el-tag :type="getStatusType(currentRecord.approval_status)" size="medium">
              {{ getStatusText(currentRecord.approval_status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="审批意见">
            <div class="comment-box">
              {{ currentRecord.approval_comment || '无' }}
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="审批时间">{{ currentRecord.start_time }}</el-descriptions-item>
        </el-descriptions>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
        <el-button
          v-if="currentRecord && currentRecord.approval_status === 1 && currentRecord.can_withdraw"
          type="warning"
          @click="handleWithdraw(currentRecord)"
        >
          撤回审批
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  apiProcessApprovalLists,
  apiProcessApprovalDetail
} from '@/api/work_order'

export default {
  name: 'ApprovalRecord',

  data () {
    return {
      searchForm: {
        order_no: '',
        handler_name: '',
        approval_status: ''
      },

      loading: false,
      tableData: [],

      pagination: {
        page: 1,
        size: 20,
        total: 0
      },

      detailDialogVisible: false,
      currentRecord: null
    }
  },

  computed: {
    // No computed properties needed
  },

  mounted () {
    this.loadTableData()
  },

  methods: {
    // 加载表格数据
    async loadTableData () {
      this.loading = true
      try {
        const params = {
          ...this.searchForm,
          page: this.pagination.page,
          size: this.pagination.size
        }

        const res = await apiProcessApprovalLists(params)
        this.tableData = res.lists || []
        this.pagination.total = res.count || 0
      } catch (error) {
        console.error('加载数据失败:', error)
      } finally {
        this.loading = false
      }
    },

    // 获取状态类型
    getStatusType (status) {
      const types = {
        1: 'primary', // 待处理
        2: 'success', // 已通过
        3: 'danger' // 已驳回
      }
      return types[status] || 'info'
    },

    // 获取状态文本
    getStatusText (status) {
      const texts = {
        1: '待处理',
        2: '已通过',
        3: '已驳回'
      }
      return texts[status] || '未知'
    },

    // 搜索
    handleSearch () {
      this.pagination.page = 1
      this.loadTableData()
    },

    // 重置
    handleReset () {
      this.searchForm = {
        order_no: '',
        handler_name: '',
        approval_status: ''
      }
      this.pagination.page = 1
      this.loadTableData()
    },

    // 查看详情
    async handleView (row) {
      try {
        this.currentRecord = row
        this.detailDialogVisible = true
      } catch (error) {
        this.$message.error('加载详情失败')
      }
    },

    // 撤回审批
    async handleWithdraw (row) {
      try {
        await this.$confirm('确定撤回该审批吗？', '提示', {
          type: 'warning'
        })

        // 模拟撤回操作
        this.$message.success('撤回成功')

        if (this.detailDialogVisible) {
          this.detailDialogVisible = false
        }

        this.loadTableData()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('撤回失败')
        }
      }
    },

    // 分页大小改变
    handleSizeChange (size) {
      this.pagination.size = size
      this.loadTableData()
    },

    // 页码改变
    handleCurrentChange (page) {
      this.pagination.page = page
      this.loadTableData()
    }
  }
}
</script>

<style lang="scss" scoped>
.approval-record-container {
  padding: 20px;

  .search-container {
    margin-bottom: 20px;
    padding: 20px;
    background: #fff;
    border-radius: 4px;
  }

  .table-container {
    background: #fff;
    border-radius: 4px;
    overflow: hidden;

    .comment-text {
      max-width: 300px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .text-muted {
      color: #909399;
    }

    .pagination-container {
      padding: 20px;
      text-align: right;
    }
  }

  .approval-detail {
    .comment-box {
      padding: 10px;
      background: #f5f7fa;
      border-radius: 4px;
      min-height: 60px;
      white-space: pre-wrap;
    }
  }
}
</style>
